import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

// Types
export interface Customer {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  address?: string;
  parish?: string;
  status: 'active' | 'inactive' | 'blocked';
  subscriptionStatus: 'free' | 'premium';
  emailVerified: boolean;
  phoneVerified: boolean;
  totalBookings: number;
  completedBookings: number;
  createdAt: string;
  updatedAt: string;
}

interface AuthState {
  customer: Customer | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface CustomerAuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
  register: (data: any) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  forgotPassword: (email: string) => Promise<{ success: boolean; message: string }>;
  resetPassword: (token: string, password: string) => Promise<{ success: boolean; message: string }>;
  updateProfile: (data: Partial<Customer>) => Promise<{ success: boolean; message: string }>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; message: string }>;
  refreshAuth: () => Promise<void>;
  loginWithGoogle: (token: string) => Promise<{ success: boolean; message: string }>;
}

// Create context
const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(undefined);

// Axios instance for authenticated requests
export const customerApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Axios instance for public requests (no auth required)
export const publicCustomerApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Auth Provider Component
interface CustomerAuthProviderProps {
  children: ReactNode;
}

export const CustomerAuthProvider: React.FC<CustomerAuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    customer: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Setup axios interceptors
  useEffect(() => {
    // Request interceptor
    const requestInterceptor = customerApi.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('skillseek_customer_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    const responseInterceptor = customerApi.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If error is 401 and we haven't tried to refresh token yet
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          // Only try to refresh if we have a token
          const token = localStorage.getItem('skillseek_customer_token');
          if (token) {
            try {
              // Try to verify token again
              await verifyToken();

              // Retry the original request
              const newToken = localStorage.getItem('skillseek_customer_token');
              if (newToken) {
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return customerApi(originalRequest);
              }
            } catch (refreshError) {
              // If refresh fails, logout
              console.log('Token refresh failed, logging out');
              logout();
              return Promise.reject(refreshError);
            }
          } else {
            // No token, logout
            logout();
          }
        }

        return Promise.reject(error);
      }
    );

    // Cleanup interceptors on unmount
    return () => {
      customerApi.interceptors.request.eject(requestInterceptor);
      customerApi.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Load token on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('skillseek_customer_token');
      if (token) {
        // Set authenticated state immediately with the token
        setAuthState(prev => ({
          ...prev,
          token,
          isAuthenticated: true,
          isLoading: true
        }));

        // Set the default authorization header
        customerApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // Verify the token in the background
        await verifyToken();
      } else {
        setAuthState(prev => ({ ...prev, isLoading: false }));
      }
    };

    initializeAuth();
  }, []);

  // Verify token on app load
  const verifyToken = async () => {
    try {
      const token = localStorage.getItem('skillseek_customer_token');
      if (!token) {
        setAuthState(prev => ({
          ...prev,
          isAuthenticated: false,
          isLoading: false
        }));
        return;
      }

      const response = await customerApi.get('/customers/profile');
      if (response.data.success) {
        setAuthState(prev => ({
          ...prev,
          customer: response.data.customer,
          token,
          isAuthenticated: true,
          isLoading: false,
        }));
      } else {
        console.log('Token verification failed:', response.data.message);
        logout();
      }
    } catch (error: any) {
      console.log('Token verification error:', error.response?.data?.message || error.message);
      if (error.response?.status === 401) {
        logout();
      } else {
        // If it's a network error, keep the user logged in but stop loading
        setAuthState(prev => ({ ...prev, isLoading: false }));
      }
    }
  };

  // Login function
  const login = async (email: string, password: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));

      const response = await publicCustomerApi.post('/customers/login', { email, password });

      if (response.data.success) {
        const { token, customer } = response.data;

        // Store token
        localStorage.setItem('skillseek_customer_token', token);
        customerApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        setAuthState({
          customer,
          token,
          isAuthenticated: true,
          isLoading: false,
        });

        return { success: true, message: response.data.message };
      } else {
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return { success: false, message: response.data.message };
      }
    } catch (error: any) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      const message = error.response?.data?.message || 'Login failed. Please try again.';
      return { success: false, message };
    }
  };

  // Login with Google function
  const loginWithGoogle = async (token: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      const response = await publicCustomerApi.post('/customers/auth/google', { token });
      
      if (response.data.success) {
        const { token: jwtToken, customer } = response.data;
        
        // Store token
        localStorage.setItem('skillseek_customer_token', jwtToken);
        customerApi.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;
        
        setAuthState({
          customer,
          token: jwtToken,
          isAuthenticated: true,
          isLoading: false,
        });
        
        return { success: true, message: response.data.message };
      } else {
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return { success: false, message: response.data.message };
      }
    } catch (error: any) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      const message = error.response?.data?.message || 'Google login failed. Please try again.';
      return { success: false, message };
    }
  };

  // Register function
  const register = async (data: any) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));

      const response = await publicCustomerApi.post('/customers/register', data);

      if (response.data.success) {
        const { token, customer } = response.data;

        // Store token
        localStorage.setItem('skillseek_customer_token', token);
        customerApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        setAuthState({
          customer,
          token,
          isAuthenticated: true,
          isLoading: false,
        });

        return { success: true, message: response.data.message };
      } else {
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return { success: false, message: response.data.message };
      }
    } catch (error: any) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      const message = error.response?.data?.message || 'Registration failed. Please try again.';
      return { success: false, message };
    }
  };

  // Logout function
  const logout = () => {
    // Clear localStorage
    localStorage.removeItem('skillseek_customer_token');

    // Clear axios defaults
    delete customerApi.defaults.headers.common['Authorization'];

    // Reset auth state
    setAuthState({
      customer: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });

    // Optional: Show a brief message that user has been logged out
    console.log('User logged out successfully');
  };

  // Forgot password function
  const forgotPassword = async (email: string) => {
    try {
      const response = await publicCustomerApi.post('/customers/forgot-password', { email });
      return { success: response.data.success, message: response.data.message };
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to send reset email. Please try again.';
      return { success: false, message };
    }
  };

  // Reset password function
  const resetPassword = async (token: string, password: string) => {
    try {
      const response = await publicCustomerApi.post('/customers/reset-password', { token, password });
      return { success: response.data.success, message: response.data.message };
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to reset password. Please try again.';
      return { success: false, message };
    }
  };

  // Update profile function
  const updateProfile = async (data: Partial<Customer>) => {
    try {
      const response = await customerApi.put('/customers/profile', data);
      if (response.data.success) {
        setAuthState(prev => ({
          ...prev,
          customer: response.data.customer,
        }));
        return { success: true, message: response.data.message };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to update profile. Please try again.';
      return { success: false, message };
    }
  };

  // Change password function
  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      const response = await customerApi.put('/customers/change-password', {
        currentPassword,
        newPassword
      });
      return { success: response.data.success, message: response.data.message };
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to change password. Please try again.';
      return { success: false, message };
    }
  };

  // Refresh auth function
  const refreshAuth = async () => {
    await verifyToken();
  };

  const value: CustomerAuthContextType = {
    ...authState,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    updateProfile,
    changePassword,
    refreshAuth,
    loginWithGoogle,
  };

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
};

// Custom hook to use customer auth context
export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (context === undefined) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};

export default CustomerAuthContext; 